'use client';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type ConsentChoice = 'accepted' | 'essential';

const STORAGE_KEY = 'cbs_cookie_consent';
const MAX_AGE_DAYS = 365;

interface StoredConsent {
  choice: ConsentChoice;
  date: string;
}

function readStored(): ConsentChoice | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed?.choice !== 'accepted' && parsed?.choice !== 'essential') return null;
    const saved = new Date(parsed.date).getTime();
    if (Number.isNaN(saved)) return null;
    const ageDays = (Date.now() - saved) / (1000 * 60 * 60 * 24);
    if (ageDays > MAX_AGE_DAYS) return null;
    return parsed.choice;
  } catch {
    return null;
  }
}

function writeStored(choice: ConsentChoice): void {
  try {
    const value: StoredConsent = { choice, date: new Date().toISOString() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage unavailable: choice applies for this visit only
  }
}

interface CookieConsentContextValue {
  ready: boolean;
  choice: ConsentChoice | null;
  bannerOpen: boolean;
  accept: () => void;
  essentialOnly: () => void;
  openBanner: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextValue>({
  ready: false,
  choice: null,
  bannerOpen: false,
  accept: () => {},
  essentialOnly: () => {},
  openBanner: () => {},
});

export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(false);
  const [choice, setChoice] = useState<ConsentChoice | null>(null);
  const [bannerOpen, setBannerOpen] = useState(false);

  useEffect(() => {
    const stored = readStored();
    setChoice(stored);
    setBannerOpen(stored === null);
    setReady(true);
  }, []);

  const accept = useCallback(() => {
    writeStored('accepted');
    setChoice('accepted');
    setBannerOpen(false);
  }, []);

  const essentialOnly = useCallback(() => {
    writeStored('essential');
    setChoice('essential');
    setBannerOpen(false);
  }, []);

  const openBanner = useCallback(() => setBannerOpen(true), []);

  const value = useMemo(
    () => ({ ready, choice, bannerOpen, accept, essentialOnly, openBanner }),
    [ready, choice, bannerOpen, accept, essentialOnly, openBanner]
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent(): CookieConsentContextValue {
  return useContext(CookieConsentContext);
}
